import type { AgentHistoryItem } from './provenance-entry-item';

export interface MergedObservation {
  observationGroupId: number;
  photoIds: number[];
  recordingIds: number[];
  videoIds: number[];
}

export interface ObservationProjection {
  /** groupId -> 'OBS-1' | 'OBS-2' | … */
  labelByGroupId: ReadonlyMap<number, string>;
  /** groupId -> every item captured into that Observation across all its submits. */
  mergedByGroupId: ReadonlyMap<number, MergedObservation>;
  /** The one row per Observation that renders the card; later rows fold into it. */
  cardItemIds: ReadonlySet<string>;
}

// OBS-n is a per-conversation display label derived from history order, not a stored
// field: the graph already has one durable identifier for an Observation (its COL- code),
// and a second persisted sequence would need a counter, collide across concurrent
// members, and drift. Derivation is stable because history order is stable.
//
// Repeat submits into one session persist one row each, but the member made one
// Observation — so the rows union into the card at the group's first appearance.
export function projectObservations(items: readonly AgentHistoryItem[]): ObservationProjection {
  const labelByGroupId = new Map<number, string>();
  const mergedByGroupId = new Map<number, MergedObservation>();
  const cardItemIds = new Set<string>();

  const MEDIA_KEYS = ['photoIds', 'recordingIds', 'videoIds'] as const;

  for (const item of items) {
    if (item.kind === 'observation_media_moved') {
      const from = mergedByGroupId.get(item.fromObservationGroupId);
      if (!from) continue;
      // A split has no capture row of its own — the destination Observation is born
      // here — so this move IS its first appearance and the row that renders its card.
      let to = mergedByGroupId.get(item.toObservationGroupId);
      if (!to) {
        labelByGroupId.set(item.toObservationGroupId, `OBS-${labelByGroupId.size + 1}`);
        cardItemIds.add(item.id);
        to = {
          observationGroupId: item.toObservationGroupId,
          photoIds: [],
          recordingIds: [],
          videoIds: [],
        };
        mergedByGroupId.set(item.toObservationGroupId, to);
      }
      for (const key of MEDIA_KEYS) {
        const at = from[key].indexOf(item.contentId);
        if (at === -1) continue;
        from[key].splice(at, 1);
        if (!to[key].includes(item.contentId)) to[key].push(item.contentId);
        break;
      }
      continue;
    }
    if (item.kind !== 'observation_media') continue;
    const { observationGroupId } = item;

    let merged = mergedByGroupId.get(observationGroupId);
    if (!merged) {
      labelByGroupId.set(observationGroupId, `OBS-${labelByGroupId.size + 1}`);
      cardItemIds.add(item.id);
      merged = { observationGroupId, photoIds: [], recordingIds: [], videoIds: [] };
      mergedByGroupId.set(observationGroupId, merged);
    }
    for (const id of item.photoIds) {
      if (!merged.photoIds.includes(id)) merged.photoIds.push(id);
    }
    for (const id of item.recordingIds) {
      if (!merged.recordingIds.includes(id)) merged.recordingIds.push(id);
    }
    for (const id of item.videoIds) {
      if (!merged.videoIds.includes(id)) merged.videoIds.push(id);
    }
  }

  // An Observation whose last item moved out is discarded on the graph, so it stops
  // being a card and stops being a move target. Its OBS-n is not reused: the labels
  // that remain keep the numbers the member has already been reading.
  for (const [groupId, merged] of [...mergedByGroupId]) {
    if (merged.photoIds.length + merged.recordingIds.length + merged.videoIds.length > 0) continue;
    mergedByGroupId.delete(groupId);
  }
  for (const item of items) {
    if (item.kind === 'observation_media') {
      if (!mergedByGroupId.has(item.observationGroupId)) cardItemIds.delete(item.id);
      continue;
    }
    if (item.kind === 'observation_media_moved') {
      if (!mergedByGroupId.has(item.toObservationGroupId)) cardItemIds.delete(item.id);
    }
  }

  return { labelByGroupId, mergedByGroupId, cardItemIds };
}

// Whether the move control should offer "New". Counted across THIS NeoChat, because a
// projection is built from one chat's items: with one item per Observation a split is
// only a relabel, so the option earns its place once some Observation holds more than one.
export function canSplitToNewObservation(projection: ObservationProjection): boolean {
  let mediaCount = 0;
  for (const merged of projection.mergedByGroupId.values()) {
    mediaCount += merged.photoIds.length + merged.recordingIds.length + merged.videoIds.length;
  }
  return mediaCount > projection.mergedByGroupId.size;
}

export interface ObservationNewThresholds {
  recordings: number | null;
  photos: number | null;
  videos: number | null;
}

export interface ObservationMediaCounts {
  recordings: number;
  photos: number;
  videos: number;
}

// Whether a submit that left an Observation holding these counts should make the NEXT
// capture default to a new one. A null lever imposes no constraint, so "every set lever is
// met" is vacuously true with none set — hence the explicit no-lever guard, without which
// every member would be reset after every submit.
export function meetsObservationNewThresholds(
  counts: ObservationMediaCounts,
  thresholds: ObservationNewThresholds
): boolean {
  const pairs = [
    [counts.recordings, thresholds.recordings],
    [counts.photos, thresholds.photos],
    [counts.videos, thresholds.videos],
  ] as const;
  const set = pairs.filter((pair): pair is readonly [number, number] => pair[1] !== null);
  if (set.length === 0) return false;
  return set.every(([count, threshold]) => count >= threshold);
}

export interface ObservationOption {
  groupId: number;
  label: string;
}

// The chat's Observations in the order they appeared, for the capture tool's selector.
export function observationOptions(projection: ObservationProjection): ObservationOption[] {
  return [...projection.mergedByGroupId.keys()].map((groupId) => ({
    groupId,
    label: projection.labelByGroupId.get(groupId) ?? '',
  }));
}
